import { Component, HostListener, OnInit } from '@angular/core';
import { AccountService } from '../../../../../services/account/account.service';
import { LocationService } from '../../../../../services/location/location.service';
import {
  CapitanLocation,
  LocationCheckInRequiredProficienciesMatchMode,
  LocationDefaultStaffSiteEventsView,
  LocationSchedulerFirstDayOfWeek
} from '../../../../../models/location';
import { Proficiency } from '../../../../../models/proficiency';
import { ProficiencyService } from '../../../../../services/proficiency/proficiency.service';
import { OrganizationConfig, XeroTrackingCategoryOption } from '../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { ApiService } from '../../../../../services/api/api.service';
import { ComponentHasUnsavedChanges } from '../../../../../unsaved-changes-guard.service';
import { AccountingSoftwareType, TaxDisplayFormat } from '../../../../../models/organization-config-enums';
import { propExists } from '../../../../../utils';

@Component({
  selector: 'app-location-settings',
  templateUrl: './location-settings.component.html',
  styleUrls: ['./location-settings.component.css']
})
export class LocationSettingsComponent implements OnInit, ComponentHasUnsavedChanges {

  public orgConfig: OrganizationConfig;
  public location: CapitanLocation;
  public selectedXeroTrackingCategoryOption: XeroTrackingCategoryOption = null;
  public xeroTrackingCategoryOptions: XeroTrackingCategoryOption[];
  // Options for the required-check-in-proficiencies picker; null while loading.
  public proficiencyList: Proficiency[] = null;

  public selectedLocationId: number;

  public errorMessage = '';
  public loadXeroTrackingCategoryOptionsErrorMessage = ''
  public loadProficienciesErrorMessage = '';
  public errors: any = {};
  public successMessage = '';

  public loadingOrgConfig = true;
  public loadingLocation = true;
  public loadingXeroTrackingCategoryOptions = false;
  public loadingSelectLocation = false;

  public locationNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public readonly TaxDisplayFormat = TaxDisplayFormat;
  public readonly AccountingSoftwareType = AccountingSoftwareType;
  public readonly LocationCheckInRequiredProficienciesMatchMode = LocationCheckInRequiredProficienciesMatchMode;

  constructor(
    public locationService: LocationService,
    public orgConfigService: OrganizationConfigService,
    public apiService: ApiService,
    private accountService: AccountService,
    private proficiencyService: ProficiencyService
  ) {}

  ngOnInit() {
    this.setInitialLocationValues();
    this.loadOrganizationConfig();
    this.loadLocation();
    this.loadProficiencies();

    this.selectedLocationId = this.accountService.getMyAccount().default_gym_id;
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  public loadOrganizationConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.orgConfigService
          .getOrganizationConfig();
        this.loadingOrgConfig = false;
      });
  }

  /**
   * Set the initial values for the location object. These will be replaced
   * once the location loads.
   */
  public setInitialLocationValues() {
    this.location = {
      id: null,
      url: '',
      name: '',
      hide_setter: null,
      time_zone: '',
      scheduler_url_path: '',
      scheduler_landing_page_text: '',
      scheduler_first_day_of_week: LocationSchedulerFirstDayOfWeek.sunday,
      default_staff_site_events_view: LocationDefaultStaffSiteEventsView.scheduledEventsList,
      capacity: null,
      open_date: null,
      combine_same_day_check_ins: false,
      check_in_required_proficiency_ids: [],
      check_in_required_proficiency_names: [],
      check_in_required_proficiencies_match_mode: LocationCheckInRequiredProficienciesMatchMode.any,
      vat_info: '',
      allow_climber_app_home_location_selection: true,
      allow_climber_app_product_selection: true,
      today_local: '',
      lightspeed_location_id: '',
      square_location_id: '',
      accounting_connection_id: null,
      accounting_connection_name: null,
      accounting_connection_software: null,
      payment_processor_connection_id: null,
      payment_processor_connection_name: null,
      mailchimp_check_in_event_name: '',
      xero_tracking_category_name: '',
      xero_tracking_category_option_name: '',
      created_at: null,
      updated_at: null,
      areas_url: ''
    };
  }

  /**
   * Give the required-check-in-proficiencies picker a private array to work
   * on: it edits its id list in place, and the default location comes from
   * LocationService's cache as a shallow copy, so without this a ticked
   * checkbox would land in the cache before Save. Also tolerates a response
   * from a server that predates the field.
   */
  private normalizeLocation(location: CapitanLocation): CapitanLocation {
    if (!location) { return location; }

    return {
      ...location,
      check_in_required_proficiency_ids: [...(location.check_in_required_proficiency_ids || [])],
    };
  }

  /**
   * Load the organization's (non-removed) proficiencies, for the
   * required-check-in-proficiencies picker.
   */
  public loadProficiencies() {
    this.loadProficienciesErrorMessage = '';

    this.proficiencyService
      .getProficiencies(1, 3000)
      .then(response => { this.proficiencyList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
          return;
        }
        // Like the Xero options loader, report locally rather than via
        // serverError, so a failure here does not take down the whole page.
        // proficiencyList stays null: the template hides the picker (and its
        // "no proficiencies" empty state) while the error is shown.
        this.loadProficienciesErrorMessage =
          'The list of proficiencies could not be loaded (error ' + reason.status + '). ' +
          'Reload the page to try again; other settings can still be saved.';
      });
  }

  /**
   * Load the user's default location.
   */
  public loadLocation() {
    this.loadingLocation = true;

    this.locationService
      .loadLocations()
      .then(() => {
        this.location = this.normalizeLocation(
          this.accountService.getDefaultLocation()
        );

        const locationServerError = this.locationService.getLocationsServerError();
        if (locationServerError) {
          this.serverError = locationServerError;
        } else if (this.location.accounting_connection_software === AccountingSoftwareType.xero) {
          this.loadXeroTrackingCategoryOptions();
        }
      })
      .finally(() => { this.loadingLocation = false; });
  }

  public handleSelectLocation() {
    this.loadingSelectLocation = true;

    this.locationService
      .getLocation(this.selectedLocationId)
      .then(loadedLoc => {
        this.location = this.normalizeLocation(loadedLoc);
        if (this.location.accounting_connection_software === AccountingSoftwareType.xero) {
          this.loadXeroTrackingCategoryOptions();
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingSelectLocation = false; });
  }

  public saveLocationSettings() {
    this.loadingLocation = true;

    this.errorMessage = '';
    this.errors = {};
    this.successMessage = '';

    if (this.location.open_date === '') {
      this.location.open_date = null;
    }

    if (!propExists(this.selectedXeroTrackingCategoryOption)) {
      this.location.xero_tracking_category_name = '';
      this.location.xero_tracking_category_option_name = '';
    } else {
      this.location.xero_tracking_category_name = this.selectedXeroTrackingCategoryOption.category_name;
      this.location.xero_tracking_category_option_name = this.selectedXeroTrackingCategoryOption.option_name;
    }

    this.locationService
      .updateLocationGeneralSettings(this.location)
      .then(updatedLocation => {
        this.unsavedChanges = false;

        this.location = updatedLocation;

        this.successMessage = 'Settings updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify these settings.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocation = false; });
  }

  public isLoading() {
    return (
      this.loadingOrgConfig ||
      this.loadingLocation ||
      this.loadingSelectLocation ||
      this.loadingXeroTrackingCategoryOptions
    );
  }

  public loadXeroTrackingCategoryOptions() {
    this.loadingXeroTrackingCategoryOptions = true;

    this.orgConfigService
      .getXeroTrackingCategoryOptions(this.location.accounting_connection_id)
      .then(result => {
        this.xeroTrackingCategoryOptions = result;
        this.xeroTrackingCategoryOptions.sort((a, b) => {
          if (a.category_name < b.category_name) { return -1; }
          if (a.category_name > b.category_name) { return 1; }
          if (a.option_name < b.option_name) { return -1; }
          if (a.option_name > b.option_name) { return 1; }
          return 0;
        })
        this.selectedXeroTrackingCategoryOption = null;
        for (const tco of this.xeroTrackingCategoryOptions) {
          if (
            (tco.category_name === this.location.xero_tracking_category_name) &&
            (tco.option_name === this.location.xero_tracking_category_option_name)
          ) {
            this.selectedXeroTrackingCategoryOption = tco;
          }
        }
      })
      .catch(reason => {
        this.loadXeroTrackingCategoryOptionsErrorMessage = 'Server error loading tracking category options: ' + reason.status.toString();
      })
      .finally(() => { this.loadingXeroTrackingCategoryOptions = false; });
  }
}
